import { Indent, MousePointerClick, LayoutPanelLeft, Link2, RefreshCw } from 'lucide-react';
import { Switch } from '../../../../components/ui/switch';
import { Label } from '../../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { useAppStore, type EditorTabSize, type TabOrientation, type FkOpenDefault } from '../../../../store/appStore';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { useBetaFeature } from '../../../../core/beta';
import { ObjectExplorerSettingsSection } from './ObjectExplorerSettingsSection';
import { ThemeModeSection } from './ThemeModeSection';
import { UiScaleSection } from './UiScaleSection';
import { EditorColorSchemeSection } from './EditorColorSchemeSection';
import { ToolbarCustomizationSection } from './toolbar/ToolbarCustomizationSection';
import { NumericGroupingSection } from './NumericGroupingSection';

const TAB_SIZE_OPTIONS: EditorTabSize[] = [2, 4, 8];

export function UISettingsTab() {
  const tabSize = useAppStore((state) => state.editorSettings.tabSize);
  const showInlineExecuteBar = useAppStore(
    (state) => state.editorSettings.showInlineExecuteBar,
  );
  const setEditorSettings = useAppStore((state) => state.setEditorSettings);
  const tabOrientation = useAppStore((state) => state.tabOrientation);
  const setTabOrientation = useAppStore((state) => state.setTabOrientation);
  const setVisualQueryEditorSettings = useAppStore(
    (state) => state.setVisualQueryEditorSettings,
  );
  const vqeRunOnChange = useAppStore(
    (state) => state.visualQueryEditorSettings.runOnChange,
  );
  // Opt-in together with the Visual Query Editor beta: the switch only means
  // something once the compact editor (where it acts) can open at all.
  const vqeEnabled = useBetaFeature('visual-query-editor');
  const fkOpenDefault = useAppStore((state) => state.fkOpenDefault);
  const setFkOpenDefault = useAppStore((state) => state.setFkOpenDefault);
  const { track } = useAnalytics();

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="text-sm font-medium">Appearance</div>

        <ThemeModeSection />

        <UiScaleSection />

        <EditorColorSchemeSection />

        <div className="flex items-center justify-between rounded-md border border-border bg-background p-4">
          <div className="flex items-center gap-3">
            <LayoutPanelLeft className="h-5 w-5 text-muted-foreground" />
            <div className="grid gap-0.5">
              <Label className="cursor-default">Tab orientation</Label>
              <div className="text-xs text-muted-foreground">
                Arrange workspace tabs horizontally across the top or vertically on the left.
              </div>
            </div>
          </div>
          <RadioGroup
            value={tabOrientation}
            onValueChange={(value) => setTabOrientation(value as TabOrientation)}
            className="ml-6 flex items-center gap-4"
            data-testid="tab-orientation-radio-group"
          >
            <div className="flex items-center gap-1.5">
              <RadioGroupItem
                id="tab-orientation-horizontal"
                value="horizontal"
                data-testid="tab-orientation-horizontal"
              />
              <Label htmlFor="tab-orientation-horizontal" className="cursor-pointer text-sm font-normal">
                Horizontal
              </Label>
            </div>
            <div className="flex items-center gap-1.5">
              <RadioGroupItem
                id="tab-orientation-vertical"
                value="vertical"
                data-testid="tab-orientation-vertical"
              />
              <Label htmlFor="tab-orientation-vertical" className="cursor-pointer text-sm font-normal">
                Vertical
              </Label>
            </div>
          </RadioGroup>
        </div>
      </div>

      <ToolbarCustomizationSection />

      <ObjectExplorerSettingsSection />

      <div className="space-y-3">
        <div className="text-sm font-medium">Editor</div>

        <div className="flex items-center justify-between rounded-md border border-border bg-background p-4">
          <div className="flex items-center gap-3">
            <Indent className="h-5 w-5 text-muted-foreground" />
            <div className="grid gap-0.5">
              <Label htmlFor="tab-size" className="cursor-pointer">
                Tab Size
              </Label>
              <div className="text-xs text-muted-foreground">
                Number of spaces inserted when you press Tab
              </div>
            </div>
          </div>
          <Select
            value={String(tabSize)}
            onValueChange={(value) =>
              setEditorSettings({ tabSize: Number(value) as EditorTabSize })
            }
          >
            <SelectTrigger
              id="tab-size"
              className="w-[90px]"
              data-testid="tab-size-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TAB_SIZE_OPTIONS.map((size) => (
                <SelectItem
                  key={size}
                  value={String(size)}
                  data-testid={`tab-size-option-${size}`}
                >
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between rounded-md border border-border bg-background p-4">
          <div className="flex items-center gap-3">
            <MousePointerClick className="h-5 w-5 text-muted-foreground" />
            <div className="grid gap-0.5">
              <Label htmlFor="inline-execute-bar" className="cursor-pointer">
                Inline Execute / Peek bar
              </Label>
              <div className="text-xs text-muted-foreground">
                Show a floating Execute / Peek bar next to selected SQL and above the statement under the cursor. When off, the cursor statement gets two small icon buttons in the gutter instead. Either way, the toolbar Execute button still relabels to "Selection" for a runnable selection.
              </div>
            </div>
          </div>
          <Switch
            id="inline-execute-bar"
            checked={showInlineExecuteBar}
            onCheckedChange={(checked) => {
              setEditorSettings({ showInlineExecuteBar: checked });
              track('feature:inline_execute_bar_toggled', { enabled: checked });
            }}
            data-testid="inline-execute-bar-toggle"
          />
        </div>
      </div>

      {vqeEnabled ? (
      <div className="space-y-3">
        <div className="text-sm font-medium">Visual editor</div>

        <div className="flex items-center justify-between rounded-md border border-border bg-background p-4">
          <div className="flex items-center gap-3">
            <RefreshCw className="h-5 w-5 text-muted-foreground" />
            <div className="grid gap-0.5">
              <Label htmlFor="vqe-run-on-change" className="cursor-pointer">
                Run SELECT after every visual edit
              </Label>
              <div className="text-xs text-muted-foreground">
                While the compact visual editor is open on a SELECT in the Query Editor, re-run the statement after each committed edit (filter, sort, column, join) so the results grid follows the query. Never applies to INSERT, UPDATE, DELETE, or MERGE. The same switch sits in the compact editor&apos;s footer, next to Done.
              </div>
            </div>
          </div>
          <Switch
            id="vqe-run-on-change"
            checked={vqeRunOnChange}
            onCheckedChange={(checked) => {
              setVisualQueryEditorSettings({ runOnChange: checked });
              track('feature:vqe_run_on_change_toggled', { enabled: checked, source: 'settings' });
            }}
            data-testid="vqe-run-on-change-toggle"
          />
        </div>
      </div>
      ) : null}

      <div className="space-y-3">
        <div className="text-sm font-medium">Behavior</div>

        <div className="flex items-center justify-between rounded-md border border-border bg-background p-4">
          <div className="flex items-center gap-3">
            <Link2 className="h-5 w-5 text-muted-foreground" />
            <div className="grid gap-0.5">
              <Label htmlFor="fk-open-default" className="cursor-pointer">
                FK &ldquo;Open&rdquo; default
              </Label>
              <div className="text-xs text-muted-foreground">
                What the foreign-key popover&apos;s primary &ldquo;Open&rdquo; button does. The ⋯ menu always exposes the alternative.
              </div>
            </div>
          </div>
          <Select
            value={fkOpenDefault}
            onValueChange={(value) => {
              const next = value as FkOpenDefault;
              if (next === fkOpenDefault) return;
              track('feature:fk_click_default_changed', { from: fkOpenDefault, to: next });
              setFkOpenDefault(next);
            }}
          >
            <SelectTrigger
              id="fk-open-default"
              className="w-[200px]"
              data-testid="fk-open-default-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="in-place" data-testid="fk-open-default-in-place">
                Drill in current tab (new)
              </SelectItem>
              <SelectItem value="new-tab" data-testid="fk-open-default-new-tab">
                Open in new tab (old)
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <NumericGroupingSection />
      </div>
    </div>
  );
}
